import {
  useIsMobile,
  useVisualViewport,
} from '@brunommsilva/react-foundations';
import { Box } from '@mui/material';
import { useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import AssistantChat from '../components/assistant/conversation/assistant-chat';
import TopBar from '../components/navigation-bar/top-bar';
import * as PATHS from '../config/paths';
import useConversation from '../hooks/assistant/use-conversation';

/**
 * The assistant on a phone: the whole screen, with the top bar of the app and no navigation at the
 * bottom. It is sized to the part of the screen that is really visible, so the field stays above the
 * keyboard and nothing moves when it opens.
 */
const AssistantPage = () => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { key } = useLocation();
  const viewport = useVisualViewport();
  const { isUnavailable } = useConversation();

  // The page itself must not scroll: the visible area is laid out by the container below.
  useEffect(() => {
    const { body, documentElement } = document;
    const previous = [
      body.style.overflow,
      documentElement.style.overscrollBehavior,
    ];

    body.style.overflow = 'hidden';
    documentElement.style.overscrollBehavior = 'none';

    return () => {
      [body.style.overflow, documentElement.style.overscrollBehavior] =
        previous;
    };
  }, []);

  // Larger screens have the floating window instead.
  if (!isMobile || isUnavailable) {
    return <Navigate to={PATHS.DASHBOARD_PATH} replace />;
  }

  // The first page of the history has nowhere to go back to.
  const goBack = () =>
    key === 'default' ? navigate(PATHS.DASHBOARD_PATH) : navigate(-1);

  return (
    <Box
      sx={{
        position: 'fixed',
        left: 0,
        right: 0,
        top: viewport?.offsetTop ?? 0,
        height: viewport?.height ?? '100dvh',
        display: 'flex',
        flexDirection: 'column',
        pt: 'var(--safe-top)',
        pb: 'var(--bottom-nav-inset)',
        bgcolor: 'background.paper',
        transform: 'translateZ(0)',
      }}
    >
      <TopBar />
      <AssistantChat variant="page" onBack={goBack} />
    </Box>
  );
};

export default AssistantPage;
